import { useEffect, useState, useMemo, FormEvent } from "react";
import { Button, Modal } from "react-bootstrap";
import { Form } from "react-bootstrap";

import { RenderFields } from "../../../shared/utils/render-fields";
import { IFieldConfigDefinition } from "../../definitions/field";
import { ITableRowData } from "../normal-table/normal-table";
import "./popup.scss";

export interface IPopupConfig {
  mode: string;
  buttonText: string;
  colums: IFieldConfigDefinition[];
  values: ITableRowData | undefined;
  show: boolean;
  rowsData: ITableRowData[],
  uniqueKeys?: string[],
  setUniqueDefaults?: any; // eslint-disable-line 
  handlePopupClosed(result: ITableRowData | undefined): void;
  renderExtraPopUpButtons?: (data: ITableRowData) => JSX.Element;
  getData?: (url: string) => void;
}

const Popup = ({ mode,show, renderExtraPopUpButtons, setUniqueDefaults, rowsData, uniqueKeys, buttonText, colums, values, getData, handlePopupClosed }: IPopupConfig ) => {
  const [data, setData] = useState<ITableRowData>({id : values? values.id : "", ...setUniqueDefaults, datetime_format: "%Y-%m-%d %H:%M:%S"});
  const [validated, setValidated] = useState(false);
  const [skipInitialFieldType, setSkipInitialFieldtype] = useState(false);
  const [skipInitialTextType, setSkipInitialTexttype] = useState(false);

  useEffect(() => {
    if (values) {
      setData({ ...values, referencing_component_id_ref: values.referencing_component_id && values.referencing_component_id });
    }
  }, [values]);

  useEffect(() => {
    const dataTypeIsInteger = data["field_datatype"] === "integer";
    const dataTypeIsDatetime = data["field_datatype"] === "datetime";
    const dataTypeIsText= data["field_datatype"] === "text";

    const copyData = {...data};

    if (!dataTypeIsInteger && !dataTypeIsText) {
      delete copyData["primary_key"];
    }

    if (!dataTypeIsText) {
      delete copyData["text_type"];
    }

    if (!dataTypeIsDatetime) {
      delete copyData["datetime_format"];
    }

    if (data.field_datatype && !skipInitialFieldType) {
      setSkipInitialFieldtype(true);
    }
    if (data.field_datatype && skipInitialFieldType) {
      if (dataTypeIsText && !copyData.text_type) {
        setData({...copyData, text_type: "short_text" });
      } else {
        setData({...copyData });
      }
    }
  }, [data.field_datatype]);

  useEffect(() => {
    const copyData = {...data};

    if (data.text_type === "long_text") {
      delete copyData["primary_key"];
    }
    if (data.text_type && !skipInitialTextType) {
      setSkipInitialTexttype(true);
    }
    if (data.text_type && skipInitialTextType) {
      setData({...copyData });
    }
  }, [data.text_type]);

  useEffect(() => {
    (skipInitialFieldType && data.referencing_component) && setData((prevState) => ({ ...prevState, referencing_component_field_id: "", referencing_component_field: "" }));
  }, [data.referencing_component]);

  const handleOnChange = (targetVal: string, inputKey: string, id: string) => {
    if (id) {
      if (inputKey === "primary_key") {
        setData(prevState => ({ ...prevState, [inputKey]: targetVal }));
      } else {
        setData(prevState => ({ ...prevState, [inputKey]: targetVal, [`${inputKey}_id`] : id }));
      }
    } else {
      if (data[`${inputKey}_id`]) {
        setData(prevState => ({ ...prevState, [inputKey]: targetVal, [`${inputKey}_id`] : "", [`${inputKey}_id_ref`] : "" }));
      } else {
        setData(prevState => ({ ...prevState, [inputKey]: targetVal }));
      }
    }
  };

  const memorizedPrint = useMemo(() => {
    if (Object.keys(data).length !== 0 && data.constructor === Object ) {     
      let copyColums = [...colums];
      const dataTypeIsDatetime = data["field_datatype"] === "datetime";

      const dataTypeIsInteger = data["field_datatype"] === "integer";
      const dataTypeIsText= data["field_datatype"] === "text";

      if (dataTypeIsDatetime) {
        copyColums = [
          ...copyColums,
          {
            key: "datetime_format",
            displayName: "Datetime format",
            dataType: {
              type: "text",
            },
            placeholder: "%Y-%m-%d %H:%M:%S",
            required: false,
            extraToolTipInfo: "The datetime format used by this field `%Y-%m-%d %H:%M:%S` (e.g., 2024-07-05 14:30:00)"
          },
        ];
      }
      
      if (!dataTypeIsText) {
        copyColums = copyColums.filter((field) => field.key !== "text_type");
      }

      if ((!dataTypeIsInteger && !dataTypeIsText) || data.text_type && data.text_type === "long_text") {
        copyColums = copyColums.filter((field) => field.key !== "primary_key");
      }
      
      copyColums = copyColums.filter((field) => field.key !== "valid");

      return <RenderFields mode={mode} getData={getData} fields={copyColums} data={data} handleOnChange={handleOnChange} />;
    }
  }, [data, colums]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    const copyData = {...data};
    event.preventDefault();

    if (form.checkValidity() !== true) {
      setValidated(true);
      return;
    }

    if (data.field_datatype !== "float") {
      setData((prevState) => ({ ...prevState, ...copyData }));
    }

    if (uniqueKeys && uniqueKeys.length !== 0 && matchUniqueValues(copyData)) {
      setValidated(true);
    } else {
      setData({ ...copyData });
      handlePopupClosed(copyData);
      setValidated(false);
    }
  };

  const matchUniqueValues = (copyData : ITableRowData) => {
    let matchedVal = false;
    uniqueKeys?.forEach((key: string) => {
      const popupUniqueKeyData = data[key];
      rowsData.forEach((row) => {
        if ((row[key] === popupUniqueKeyData && row.id !== data.id) || row[key] === popupUniqueKeyData && !data.id) {
          matchedVal = true;
          setData({ ...copyData, [key] : "" });
        }
      });
    });
    return matchedVal;
  };

  return (
    <div className="popup">
      <Modal show={show} onHide={() => handlePopupClosed(undefined)}>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{mode}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {memorizedPrint}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => handlePopupClosed(undefined)}
            >
              Close
            </Button>
            {renderExtraPopUpButtons && renderExtraPopUpButtons(data)}
            <Button
              className="popup-action"
              type="submit"
              variant="primary"
            >
              {buttonText}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Popup;